import { gql } from '@provision/graphql-client';
import { QueryVariables, Records } from 'src/graphql/types';
import type { StartedSmsParticipation } from './sms-session-context';

export interface ConversationSmsSessionRecord {
  conversationSmsSessionId: string;
  conversationId: string;
  state: string | null;
  openedAt: string | null;
  externalId: string | null;
}

export interface GetConversationSmsSessionQuery {
  getConversationSmsSession: Records<ConversationSmsSessionRecord>;
}

export const GET_OPEN_SMS_SESSION_QUERY = gql`
  query GetOpenSmsSession($params: QueryParams) {
    getConversationSmsSession(queryParams: $params) {
      records {
        conversationSmsSessionId
        conversationId
        state
        openedAt
        externalId
      }
    }
  }
`;

export function getOpenSmsSessionQueryVariables(conversationId: string): QueryVariables {
  return {
    params: {
      filters: [
        { attribute: 'conversationId', operator: '=', value: conversationId },
        { attribute: 'state', operator: '=', value: 'open' },
      ],
    },
  };
}

export const END_SMS_SESSION_MUTATION = gql`
  mutation EndSmsSession($input: EndSmsSessionInput!) {
    endSmsSession(input: $input) {
      conversationSmsSession {
        conversationSmsSessionId
        state
        endedAt
      }
    }
  }
`;

export interface EndSmsSessionMutationResult {
  endSmsSession: {
    conversationSmsSession: {
      conversationSmsSessionId: string;
      state: string;
      endedAt: string | null;
    };
  };
}

export interface EndSmsSessionMutationVars {
  input: { conversationSmsSessionId: string };
}

export const START_SMS_SESSION_MUTATION = gql`
  mutation StartSmsSession($input: StartSmsSessionInput!) {
    startSmsSession(input: $input) {
      conversationSmsSession {
        conversationSmsSessionId
        state
        openedAt
        externalId
      }
      smsParticipations {
        conversationChannelId
        externalIdentifier
        internalIdentifier
        participantEntity
        participantIdentity
      }
    }
  }
`;

export interface StartSmsSessionMutationResult {
  startSmsSession: {
    conversationSmsSession: {
      conversationSmsSessionId: string;
      state: string;
      openedAt: string | null;
      externalId: string | null;
    };
    smsParticipations: StartedSmsParticipation[];
  };
}

export interface StartSmsSessionMutationVars {
  input: { conversationId: string; recipients: SmsSessionRecipientInput[] };
}

export const CHECK_SMS_DELIVERABILITY_MUTATION = gql`
  mutation CheckSmsDeliverability($input: CheckSmsDeliverabilityInput!) {
    checkSmsDeliverability(input: $input) {
      lineType
      lineTypeCheckedAt
      sendable
    }
  }
`;

export interface CheckSmsDeliverabilityMutationVars {
  input: { conversationChannelId?: string; clientPhoneNumberId?: string };
}

export interface CheckSmsDeliverabilityMutationResult {
  checkSmsDeliverability: {
    lineType: string | null;
    lineTypeCheckedAt: string | null;
    sendable: boolean;
  };
}

export interface SmsSessionRecipientInput {
  conversationChannelId?: string;
  clientPhoneNumberId?: string;
  phoneNumber?: string;
}

/** every session of the conversation, who it texts and from which line */
export const GET_SMS_SESSIONS_WITH_RECIPIENTS_QUERY = gql`
  query GetSmsSessionsWithRecipients($params: QueryParams) {
    getConversationSmsSession(queryParams: $params) {
      records {
        conversationSmsSessionId
        partnerPhoneNumber
        recipients {
          id
          conversationChannelId
          deletedAt
        }
      }
    }
  }
`;

export interface SmsSessionWithRecipients {
  conversationSmsSessionId: string;
  partnerPhoneNumber: string | null;
  /** deletedAt set = dropped from the session; the nested relation is not filtered server-side */
  recipients: { conversationChannelId: string; deletedAt: string | null }[] | null;
}

export interface GetSmsSessionsWithRecipientsQuery {
  getConversationSmsSession: Records<SmsSessionWithRecipients>;
}

export function getSmsSessionsWithRecipientsQueryVariables(
  conversationId: string,
): QueryVariables {
  return {
    params: {
      filters: [{ attribute: 'conversationId', operator: '=', value: conversationId }],
      // newest first so the cap keeps the sessions the timeline shows
      sorting: [{ attribute: 'createdAt', desc: true }],
      limit: 100,
    },
  };
}

export const GET_SMS_SESSION_LINE_QUERY = gql`
  query GetSmsSessionLine($input: GetSmsSessionLineInput!) {
    getSmsSessionLine(input: $input) {
      phoneNumber
      brandName
    }
  }
`;

export interface GetSmsSessionLineQuery {
  getSmsSessionLine: { phoneNumber: string; brandName: string | null };
}

export interface GetSmsSessionLineVars {
  input: { conversationId: string };
}
